/** @mui/material imports */
/** @mui/icons-material imports */
/** React imports */
import {
  Context,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
/** Goldspot imports */
import {
  DatasetStorage,
  DEBOUNCED_REMOTE_UPDATE_TIME,
  SAMPLE_DATASET_SIZE,
  SYSTEM_AVGDEPTH_COL,
} from '../TypeScript/constants';
import { errorHandler } from '../utils/error/error-handler';
import { uploadFileToS3 } from '../utils/upload-file-api';
import { appContext } from '../App';
import {
  getAllLogDataItems,
  getLog,
  putLog,
} from '../utils/authenticated-requests/logs';
import { TessellationFormContext } from './TessellationFormContext';
import { getFile } from '../utils/authenticated-requests/files';
import { logsToDataframe } from '../utils/helpers/logs-to-dataframe';
import { emptyMetadata, useMetadata } from './DatasetContext/useMetadata';
import {
  DatasetStorageType,
  JsonDataframe,
  JsonDataframeColumn,
} from '../TypeScript/types';
import {
  DatasetFile,
  DatasetLogInfo,
  DatasetMetadataCloud,
  DatasetMetadataLogsApi,
  DatasetMetadataType,
  DatasetRow,
  FileRef,
  JsonSchemaTypeObject,
  LogsApiDataItemCustom,
  SignalMap,
  TessellationJobMetaData,
  TessellationRequestParameters,
} from '../TypeScript/interfaces';
import { ColumnHeader } from '@goldspotdiscoveries/atoms/lib/upload-logs-modal/vars/types';
import { LogsApiLogObject } from '@goldspotdiscoveries/atoms';
import { getInjestedDataframe } from './DatasetContext/getInjestedDataframe';
import { getColumnHeaderPredictions } from './DatasetContext/getColumnHeaderPredictions';
import { getPathPrefix } from './DatasetContext/getPathPrefix';
import { putDataframeInLog } from '../utils/helpers/putDataframeInLog';
import { getSampleDataset } from '../utils/helpers/getSampleDataset';
import { calculateMidpointDepths } from './DatasetContext/calculateMidpointDepths';
import { useTranslation } from 'react-i18next';
import { useWaiting } from './DatasetContext/useWaiting';

export interface NewDatasetContextType {
  loadedDataset: JsonDataframe;
  metadata: DatasetMetadataType;
  updateMetadata: (metadata: DatasetMetadataType) => void;
  createDataset: (
    dataset: JsonDataframe,
    inputMetadata: DatasetMetadataType,
  ) => void;
  loadDataset: (
    datasetAccessProperties: { logId: string } | { filePath: string },
  ) => void;
  unloadDataset: () => void;
  sampleDataset: Record<ColumnHeader, DatasetRow>;
  resampleDataset: () => void;
  createRatioColumn: (
    ratioKey: string,
    visibleKey: string,
    elementColumns: ColumnHeader[],
  ) => Promise<boolean>;
  waiting: boolean;
  startTrackingProcess: () => string;
  stopTrackingProcess: (refId: string) => void;
  alterSignalsMap: (
    mappingsToAdd: Record<ColumnHeader, SignalMap>,
    mappingsToRemove?: Record<ColumnHeader, SignalMap>,
  ) => void;
  datasetType: DatasetStorageType;
  changeDatasetType: (datasetType: DatasetStorageType) => void;
  getRequestFileRef: (
    datasetType: DatasetStorageType,
    inputSignals: ColumnHeader[],
    params: TessellationRequestParameters,
  ) => Promise<FileRef>;
  getRequestSourceMetadata: () => TessellationJobMetaData;
}

export const DatasetContext: Context<NewDatasetContextType> =
  createContext<NewDatasetContextType>({
    loadedDataset: {} as JsonDataframe,
    metadata: {} as DatasetMetadataType,
    updateMetadata: (metadata: DatasetMetadataType): void => {},
    createDataset: (
      dataset: JsonDataframe,
      inputMetadata: DatasetMetadataType,
    ): void => {},
    loadDataset: (
      datasetAccessProperties: { logId: string } | { filePath: string },
    ): void => {},
    unloadDataset: (): void => {},
    sampleDataset: {},
    resampleDataset: () => {},
    createRatioColumn(ratioKey, visibleKey, elementColumns): Promise<boolean> {
      return new Promise(() => {});
    },
    waiting: false,
    startTrackingProcess: () => '',
    stopTrackingProcess: (refId: string): void => {},
    alterSignalsMap(mappingsToAdd, mappingsToRemove) {
      return;
    },
    datasetType: DatasetStorage.local,
    changeDatasetType: (datasetType: DatasetStorageType): void => {},
    getRequestFileRef: (
      datasetType: DatasetStorageType,
      inputSignals: ColumnHeader[],
      params: TessellationRequestParameters,
    ): Promise<FileRef> => Promise.resolve({ fileRef: '' }),
    getRequestSourceMetadata: (): TessellationJobMetaData => {
      return { clientFileModified: -1, clientFileName: '' };
    },
  });

export interface DatasetContextProviderProps {
  children: JSX.Element;
}

export const DatasetContextProvider: React.FC<DatasetContextProviderProps> = (
  props: DatasetContextProviderProps,
) => {
  const { t } = useTranslation();
  const { activeProject, addAppAlert, company, user, setAppLoading } =
    useContext(appContext);
  const {
    bhidCol,
    depthCol,
    depthMinCol,
    depthMaxCol,
    resetTessellationForm,
    setBhidCol,
    setDepthCol,
    setDepthMinCol,
    setDepthMaxCol,
  } = useContext(TessellationFormContext);
  const [datasetType, setDatasetType] = useState<DatasetStorageType>(
    DatasetStorage.log,
  );
  const [loadedDataset, setLoadedDataset] = useState<JsonDataframe>({});
  const [sampleDataset, setSampleDataset] = useState<
    Record<ColumnHeader, DatasetRow>
  >({});
  const datasetInitialized = useRef<boolean>(false);
  const loadedDatasetRef = useRef<JsonDataframe>({});
  const debouncedUpdateLogDatasetRef = useRef<NodeJS.Timeout>();
  const debouncedUpdateCloudDatasetRef = useRef<NodeJS.Timeout>();

  const {
    alterSignalsMap,
    getMetadata,
    metadata,
    updateMetadata,
    unloadMetadata,
  } = useMetadata({
    dataset: loadedDataset,
    datasetType,
    bhidCol,
    depthCol,
    depthMinCol,
    depthMaxCol,
    setBhidCol,
    setDepthCol,
    setDepthMinCol,
    setDepthMaxCol,
  });

  const { waiting, startTrackingProcess, stopTrackingProcess } = useWaiting();

  const updateTessellationContext = (
    predictions: Record<string, any>,
  ): void => {
    // use column predictions to get tessellation form pre-selected values
    if (predictions.bhidCol) {
      setBhidCol(predictions.bhidCol);
    }
    if (predictions.depthCol) {
      setDepthCol(predictions.depthCol);
    }
    if (predictions.depthMinCol) {
      setDepthMinCol(predictions.depthMinCol);
    }
    if (predictions.depthMaxCol) {
      setDepthMaxCol(predictions.depthMaxCol);
    }
  };

  /**
   * Makes a ratio column and adds it to the dataset state, resolving when done
   * @param ratioKey
   * @param visibleKey
   * @param elementColumns
   * @returns true after ratio column is created
   */
  const createRatioColumn = (
    ratioKey: string,
    visibleKey: string,
    elementColumns: ColumnHeader[],
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      getRatioColumn(ratioKey, elementColumns)
        .then((ratioColumn: JsonDataframeColumn) => {
          const updatedDataset: JsonDataframe = { ...loadedDataset };
          updatedDataset[visibleKey] = ratioColumn;
          updateLoadedDataset(updatedDataset);
          resolve(true);
        })
        .catch((err: any) => {
          reject(err);
        });
    });
  };

  /**
   * Creates ratio column in a web worker; resolves with the new column
   */
  const getRatioColumn = (
    ratioKey: string,
    elementColumns: ColumnHeader[],
  ): Promise<JsonDataframeColumn> => {
    const units: string[] = elementColumns.map(
      (columnHeader: ColumnHeader): string => {
        return metadata.signals?.[columnHeader].signalMeasure as string;
      },
    );
    return new Promise((resolve, reject) => {
      const worker: Worker = new Worker('/create-ratio-column.js');
      const columns: JsonDataframeColumn[] = [];
      elementColumns.forEach((elementColumn: ColumnHeader) => {
        columns.push(loadedDataset[elementColumn]);
      });
      worker.onmessage = (event: MessageEvent<JsonDataframeColumn>) => {
        const ratioColumn: JsonDataframeColumn = event.data;
        worker.terminate();
        resolve(ratioColumn);
      };
      worker.onerror = (event: ErrorEvent) => {
        worker.terminate();
        reject();
      };
      worker.postMessage({
        ratioKey,
        columns,
        units,
      });
    });
  };

  const saveParsedFile = (
    dataset: JsonDataframe,
    pathPrefix: string,
  ): Promise<string> => {
    const path: string = `${pathPrefix}/parsed.json`;
    const parsedFile = new File([JSON.stringify(dataset)], 'parsed.json', {
      type: 'application/json',
    });
    return new Promise((resolve, reject) => {
      const uploadTrackingRef = startTrackingProcess();
      uploadFileToS3(parsedFile, path, user)
        .then((hash: string) => {
          stopTrackingProcess(uploadTrackingRef);
          resolve(hash);
        })
        .catch((err) => {
          stopTrackingProcess(uploadTrackingRef);
          handleFailedDatasetUpload();
          reject(err.error);
        });
    });
  };

  /**
   * When saving dataset to cloud fails, re-sync the local dataset
   */
  const handleFailedDatasetUpload = (): void => {
    const updatedSignalsMap: Record<ColumnHeader, SignalMap> = {};
    const updatedColumnHeaders: ColumnHeader[] = [];
    let changed: boolean = false;
    metadata.columnHeaders.forEach((columnHeader: ColumnHeader) => {
      if (loadedDatasetRef.current?.hasOwnProperty(columnHeader)) {
        updatedColumnHeaders.push(columnHeader);
        if (metadata.signals && metadata.signals[columnHeader]) {
          updatedSignalsMap[columnHeader] = metadata.signals[columnHeader];
        }
      } else {
        changed = true;
        delete loadedDatasetRef.current?.[columnHeader];
      }
    });
    if (changed) {
      updateMetadata({
        ...metadata,
        signals: updatedSignalsMap,
        columnHeaders: updatedColumnHeaders,
        columnCount: updatedColumnHeaders.length,
      });
      updateLoadedDataset({ ...loadedDatasetRef.current });
    }
  };

  const resampleDataset = async (): Promise<void> => {
    try {
      const sample: JsonDataframe = await getSampleDataset(loadedDataset);
      if (sample) {
        setSampleDataset(sample);
      }
    } catch (err: any) {
      errorHandler(err);
    }
  };

  const addCalculatedDepthToDataset = (
    depthMinCol: ColumnHeader,
    depthMaxCol: ColumnHeader,
  ): void => {
    const midpointDepths: JsonDataframeColumn = calculateMidpointDepths(
      loadedDataset,
      depthMinCol,
      depthMaxCol,
    );
    const updatedDataset: JsonDataframe = Object.assign({}, loadedDataset);
    updatedDataset[SYSTEM_AVGDEPTH_COL] = midpointDepths;
    updateLoadedDataset(updatedDataset);
  };

  const unloadDataset = (): void => {
    setLoadedDataset({});
    loadedDatasetRef.current = {};
    datasetInitialized.current = false;
    unloadMetadata();
    resetTessellationForm();
  };

  /**
   * Initialize the dataset in the Logs API
   * @param dataset
   * @returns id of newly created log
   * @private
   */
  const createLogDataset = async (
    dataset: JsonDataframe,
    metadata: DatasetMetadataLogsApi,
  ): Promise<LogsApiLogObject> => {
    try {
      const schema: JsonSchemaTypeObject = {
        type: 'object',
        properties: {},
      };
      const request = {
        type: 'custom',
        description: metadata?.log?.id || '',
        depthOverlap: false,
        depthUnit: 'M',
        schema,
      };
      request.schema = schema;
      metadata.columnHeaders.forEach((columnHeader: string) => {
        request.schema.properties[columnHeader] = {
          type: 'string',
        };
      });
      const log: LogsApiLogObject = await putLog(
        company,
        activeProject.id || '',
        request,
      );
      await putDataframeInLog(dataset, log.id, activeProject.id || '', company);
      return log;
    } catch (err: any) {
      errorHandler(err);
      return {} as LogsApiLogObject;
    }
  };

  /**
   * Initialize the dataset in the Cloud Storage
   * @param dataset
   * @returns file name and hash
   * @private
   */
  const createCloudDataset = (
    dataset: JsonDataframe,
    fileInfo: DatasetFile,
  ): Promise<{ filePath: string; hash: string }> => {
    return new Promise((resolve, reject) => {
      const pathPrefix: string = getPathPrefix(
        fileInfo.name,
        activeProject.company || '',
        fileInfo.project,
      );
      const path: string = `${pathPrefix}/parsed.json`;
      const parsedFile = new File([JSON.stringify(dataset)], 'parsed.json', {
        type: 'application/json',
      });
      uploadFileToS3(parsedFile, path, user)
        .then((hash: string) => {
          resolve({ filePath: pathPrefix, hash });
        })
        .catch((err) => {
          reject(err.error);
        });
    });
  };

  /**
   * Format dataset and build metadata object
   * @param dataset
   * @returns The dataset as a dataframe and the base metadata
   * @private
   */
  const parseDataset = async (
    inputDataframe: JsonDataframe,
  ): Promise<{ dataframe: JsonDataframe; metadata: DatasetMetadataType }> => {
    const { dataframe, metadata } = await getInjestedDataframe(inputDataframe);
    const predictions = await getColumnHeaderPredictions(
      metadata.columnHeaders,
    );
    metadata.signals = predictions.inputSignals;
    metadata.dataset = {
      bhidCol: predictions.bhidCol,
      depthCol: predictions.depthCol,
      depthMaxCol: predictions.depthMaxCol,
      depthMinCol: predictions.depthMinCol,
    };
    updateTessellationContext(metadata.dataset);
    // add calculated depth column if depth from and to are predicted
    if (predictions.depthMinCol && predictions.depthMaxCol) {
      const midpoints: JsonDataframeColumn = calculateMidpointDepths(
        dataframe,
        predictions.depthMinCol,
        predictions.depthMaxCol,
      );
      dataframe[SYSTEM_AVGDEPTH_COL] = midpoints;
    }
    return { dataframe, metadata };
  };

  /**
   * Executes applicable create function depending on dataset storage setting
   * @param csv array of rows
   * @async
   */
  const createDataset = async (
    inputDataframe: JsonDataframe,
    inputMetadata: DatasetMetadataType,
  ): Promise<void> => {
    unloadDataset(); // clear any dataset currently loaded
    const { dataframe, metadata } = await parseDataset(inputDataframe);
    Object.assign(metadata, inputMetadata);
    if (dataframe && metadata) {
      if (datasetType === DatasetStorage.cloud) {
        const { filePath, hash } = await createCloudDataset(
          dataframe,
          metadata.file as DatasetFile,
        );
        (metadata as DatasetMetadataCloud).cloud = {
          path: filePath,
          hash,
        };
      } else if (datasetType === DatasetStorage.log) {
        const {
          createdAt,
          createdBy,
          id: logId,
          description,
          updatedAt,
        } = await createLogDataset(
          dataframe,
          metadata as DatasetMetadataLogsApi,
        );
        const logInfo: DatasetLogInfo = {
          id: logId,
          description: description || '',
          createdBy,
          updatedAt: new Date(updatedAt || createdAt).valueOf(),
        };
        (metadata as DatasetMetadataLogsApi).log = logInfo;
      } else if (datasetType === DatasetStorage.local) {
        // do nothing
      }
      updateMetadata(metadata);
      updateLoadedDataset(dataframe);
    }
  };

  /**
   * Update the dataset in the logs api
   * @param dataset
   * @private
   */
  const syncLogDataset = (dataset: JsonDataframe): void => {
    if (debouncedUpdateLogDatasetRef.current) {
      clearTimeout(debouncedUpdateLogDatasetRef.current);
    }
    setTimeout(() => {
      const metadata = getMetadata();
      const pathPrefix: string = getPathPrefix(
        metadata.log?.id || '',
        activeProject.company || '',
        activeProject.id,
      );
      saveParsedFile(dataset, pathPrefix);
    }, DEBOUNCED_REMOTE_UPDATE_TIME);
  };

  /**
   * Update the dataset in the cloud storage
   * @param dataset
   * @private
   */
  const syncCloudDataset = (dataset: JsonDataframe): void => {
    if (debouncedUpdateCloudDatasetRef.current) {
      clearTimeout(debouncedUpdateCloudDatasetRef.current);
    }
    setTimeout(() => {
      const pathPrefix: string = getPathPrefix(
        metadata.file?.name || '',
        activeProject.company || '',
        activeProject.id,
      );
      saveParsedFile(dataset, pathPrefix);
    }, DEBOUNCED_REMOTE_UPDATE_TIME);
  };

  const getLogMetadataFromFile = async (
    logId: string,
  ): Promise<DatasetMetadataType> => {
    try {
      const pathPrefix: string = getPathPrefix(
        logId,
        activeProject.company || '',
        activeProject.id,
      );
      const metadata: DatasetMetadataType = await getFile(
        `${pathPrefix}/metadata.json`,
      );
      return metadata;
    } catch (err) {
      return emptyMetadata;
    }
  };

  /**
   * Get the selected dataset from the logs api
   * @param logId
   * @returns JsonDataframe dataset and the dataset metadata
   * @private
   */
  const getLogDataset = async (
    logId: string,
  ): Promise<{
    dataset: JsonDataframe;
    metadata: DatasetMetadataType;
    isNew: boolean;
  } | void> => {
    const getLogDataProcessTracker = startTrackingProcess();
    try {
      // if S3 version doesn't exist, create it
      const { id, description, createdBy, updatedAt } = await getLog(
        company,
        activeProject.id ?? '',
        logId,
      );
      // attempt to fetch previous log dataset metadata from S3
      const previousMetadata: DatasetMetadataType =
        await getLogMetadataFromFile(id);
      const logDataItems = await getAllLogDataItems(
        company,
        activeProject.id ?? '',
        logId,
      );
      if (logDataItems.items.length > 0) {
        const dataframe = logsToDataframe(
          logDataItems.items as LogsApiDataItemCustom[],
        );
        const parsedDataset = await parseDataset(dataframe);
        const dataset = parsedDataset.dataframe;
        const metadata: DatasetMetadataType = {
          rowCount: parsedDataset.metadata.rowCount,
          columnCount: parsedDataset.metadata.columnCount,
          columnHeaders:
            previousMetadata.columnHeaders.length > 0
              ? previousMetadata.columnHeaders
              : parsedDataset.metadata.columnHeaders,
          signals:
            previousMetadata.signals &&
            Object.keys(previousMetadata.signals).length > 0
              ? previousMetadata.signals
              : parsedDataset.metadata.signals,
          dataset:
            previousMetadata.signals &&
            Object.keys(previousMetadata.signals).length > 0
              ? previousMetadata.dataset
              : parsedDataset.metadata.dataset,
          log: {
            id,
            description: description || '',
            createdBy,
            updatedAt: updatedAt ? new Date(updatedAt).valueOf() : -1,
          },
        };
        const isNew: boolean = !previousMetadata?.columnCount;
        return {
          dataset,
          metadata,
          isNew,
        };
      } else {
        addAppAlert({
          message: t('ALERT_ERROR_LOGS_ZEROLENGTH', {
            log: description || id,
          }),
          severity: 'error',
        });
      }
    } catch (err: any) {
      errorHandler(err);
      throw new Error(err);
    } finally {
      stopTrackingProcess(getLogDataProcessTracker);
    }
  };

  /**
   * Get the selected dataset from the cloud storage
   * @param filePath
   * @returns  JsonDataframe dataset and the dataset metadata
   * @private
   */
  const getCloudDataset = (
    filePath: string,
  ): Promise<{ dataset: JsonDataframe; metadata: DatasetMetadataType }> => {
    return new Promise((resolve, reject) => {
      const parsedPath: string = `${filePath}/parsed.json`;
      const metadataPath: string = `${filePath}/metadata.json`;
      const promises: Promise<any>[] = [
        getFile(parsedPath),
        getFile(metadataPath),
      ];
      Promise.all(promises)
        .then((response: any[]) => {
          const parsed: Record<ColumnHeader, DatasetRow> = response[0];
          const metadata: DatasetMetadataType = response[1];
          if (parsed && metadata) {
            resolve({ dataset: parsed, metadata });
          }
        })
        .catch((err) => {
          errorHandler(err);
        });
    });
  };

  /**
   * Load dataset that user selects to the app
   * @param datasetAccessProperties
   */
  const loadDataset = async (
    datasetAccessProperties: Partial<{ logId: string; filePath: string }>,
  ): Promise<void> => {
    const loadDatasetProcessTracking = startTrackingProcess();
    try {
      unloadDataset();
      const NOSYNC = true;
      if (datasetAccessProperties.logId) {
        setDatasetType(DatasetStorage.log);
        const logDataset = await getLogDataset(datasetAccessProperties.logId);
        if (!logDataset) {
          throw new Error();
        }
        if (logDataset.metadata.dataset) {
          updateTessellationContext(logDataset.metadata.dataset);
        }
        updateMetadata(logDataset.metadata, !logDataset.isNew);
        updateLoadedDataset(logDataset.dataset, !logDataset.isNew);
      } else if (datasetAccessProperties.filePath) {
        setDatasetType(DatasetStorage.cloud);
        const cloudDataset = await getCloudDataset(
          datasetAccessProperties.filePath,
        );
        if (cloudDataset.metadata.dataset) {
          updateTessellationContext(cloudDataset.metadata.dataset);
        }
        updateMetadata(cloudDataset.metadata, NOSYNC);
        updateLoadedDataset(cloudDataset.dataset, NOSYNC);
      }
    } catch (err: any) {
      errorHandler(err);
    } finally {
      stopTrackingProcess(loadDatasetProcessTracking);
    }
  };

  const getRequestFile = (
    inputSignals: ColumnHeader[],
    params: TessellationRequestParameters,
  ): File => {
    const neededColumns: string[] = [];
    const dataset: JsonDataframe = {};
    // Removing unnecessary columns to reduce payload
    if (
      bhidCol &&
      params.depth_col &&
      params.depth_min_col &&
      params.depth_max_col
    ) {
      neededColumns.push(
        bhidCol,
        params.depth_col,
        params.depth_min_col,
        params.depth_max_col,
        ...inputSignals,
      );
    }
    neededColumns.forEach((column) => {
      if (column && loadedDataset[column]) {
        dataset[column] = loadedDataset[column];
      }
    });
    const data: string = JSON.stringify(dataset);
    const file: File = new File([data], 'requestDataset.json', {
      type: 'text/plain',
    });
    return file;
  };

  const putRequestFileRef = async (file: File): Promise<string> => {
    const uploadTrackingRef = startTrackingProcess();
    try {
      const timeStamp: number = new Date().valueOf();
      const pathName: string = `${company}/${activeProject.id}/tessellation/files/requests/${metadata.file?.name}/requestDataset.${timeStamp}.json`;
      await uploadFileToS3(file, pathName, user);
      stopTrackingProcess(uploadTrackingRef);
      return pathName;
    } catch (err: any) {
      errorHandler(err);
      stopTrackingProcess(uploadTrackingRef);
      throw new Error(err);
    }
  };

  const getRequestFileRef = async (
    datasetType: DatasetStorageType,
    inputSignals: ColumnHeader[],
    params: TessellationRequestParameters,
  ): Promise<FileRef> => {
    try {
      const file: FileRef = {
        fileRef: '',
      };
      switch (datasetType) {
        case DatasetStorage.local:
          const requestFile: File = getRequestFile(inputSignals, params);
          const pathName: string = await putRequestFileRef(requestFile);
          file.fileRef = pathName;
          break;
        case DatasetStorage.log:
          const logPathPrefix: string[] = getPathPrefix(
            metadata.log?.id || '',
            activeProject.company || '',
            activeProject.id,
          ).split('');
          logPathPrefix.shift();
          const logPath: string = `${logPathPrefix.join('')}/parsed.json`;
          file.fileRef = logPath;
          break;
        case DatasetStorage.cloud:
          // remove leading backslash from cloud path
          const basePath: string[] = (metadata.cloud?.path || '').split('');
          basePath.shift();
          const fileRef: string = `${basePath.join('')}/parsed.json`;
          file.fileRef = fileRef;
          break;
      }
      return file;
    } catch (err: any) {
      throw new Error(err);
    }
  };

  /**
   * Get the dataset source info for tessellation job requests
   * @returns
   */
  const getRequestSourceMetadata = (): TessellationJobMetaData => {
    const requestSourceMetadata: TessellationJobMetaData = {
      clientFileName: metadata.file?.name || '',
      clientFileModified: metadata.file?.lastModified || -1,
    };
    if (metadata.cloud?.hash && metadata.cloud?.path) {
      requestSourceMetadata.cloudFileLocation = metadata.cloud.path;
      requestSourceMetadata.datasetHash = metadata.cloud.hash;
    } else if (datasetType === DatasetStorage.local) {
      requestSourceMetadata.clientFileName = metadata.file?.name || '';
      requestSourceMetadata.clientFileModified =
        metadata.file?.lastModified || -1;
    } else if (datasetType === DatasetStorage.log) {
      requestSourceMetadata.clientFileName =
        metadata.log?.description || metadata.log?.id || '';
      requestSourceMetadata.clientFileModified = metadata.log
        ? metadata.log.updatedAt
        : -1;
      requestSourceMetadata.logId = metadata.log?.id;
    }
    return requestSourceMetadata;
  };

  /**
   * Set datasetType in context state, and reset the dataset and metadata
   * @param selection
   */
  const changeDatasetType = (selection: DatasetStorageType): void => {
    setDatasetType(selection);
    // when user changes dataset source, clear dataset and metadata in app
    unloadDataset();
  };

  /**
   * Update the dataset in memory and, optionally, the remote version
   *
   * - updates dataset sample (if applicable)
   * - marks dataset as initialized in ref
   * - updates dataset ref (for future comparisons)
   *
   * @param updatedDataset the new version of the dataset to put in state
   * @param noSync if true, skip updating remote version of dataset @default false (do update remote)
   */
  const updateLoadedDataset = (
    updatedDataset: JsonDataframe,
    noSync = false,
  ): void => {
    setLoadedDataset(updatedDataset);
    if (!noSync) {
      if (datasetType === DatasetStorage.log) {
        syncLogDataset(updatedDataset);
      } else if (datasetType === DatasetStorage.cloud) {
        syncCloudDataset(updatedDataset);
      }
    }
    if (Object.keys(updatedDataset).length > 0) {
      if (
        !datasetInitialized.current ||
        updatedDataset !== loadedDatasetRef.current
      ) {
        if (metadata.rowCount > SAMPLE_DATASET_SIZE) {
          resampleDataset();
        }
      }
      datasetInitialized.current = true;
    }
    loadedDatasetRef.current = updatedDataset;
  };

  // replace calculated depth column when user changes depth columns
  useEffect(() => {
    if (
      depthMinCol &&
      depthMaxCol &&
      loadedDataset[depthMinCol] &&
      loadedDataset[depthMaxCol]
    ) {
      addCalculatedDepthToDataset(depthMinCol, depthMaxCol);
    }
  }, [depthMinCol, depthMaxCol]);

  // update app loading state with useWaiting
  useEffect(() => {
    setAppLoading(waiting);
  }, [waiting]);

  return (
    <>
      <DatasetContext.Provider
        value={{
          loadedDataset,
          metadata,
          updateMetadata,
          createDataset,
          loadDataset,
          unloadDataset,
          sampleDataset,
          resampleDataset,
          createRatioColumn,
          waiting,
          startTrackingProcess,
          stopTrackingProcess,
          alterSignalsMap,
          datasetType,
          changeDatasetType,
          getRequestFileRef,
          getRequestSourceMetadata,
        }}
      >
        {props.children}
      </DatasetContext.Provider>
    </>
  );
};
